// llbrary components
import React, { Component, Fragment, useEffect, useState } from "react";
import { Link, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { Table, DatePicker, Upload, notification, Popconfirm } from "antd";
import dayjs from "dayjs";
import { PlusOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";

// common components
import H3Text from "@Uilib/h3Text";
import H3Button, { defaultButtonTypes } from "@Uilib/h3Button";
import H3FormInput from "@Uilib/h3FormInput";
import WarehouseLocationSelector from "@Components/Common/Selector/WarehouseLocationSelector";
import ProductCategoryLabel from "../../../Common/ProductCategoryLabel";
import CustomFieldV3 from "@Components/Common/CustomFieldV3";
import ErrorHandle from "../../../Common/ErrorHandle";
import DocumentNumberSeqInput from "../../../Admin/Common/DocumentNumberSeqInput";
import "./style.scss";

// actions
import PurchaseOrderActions from "@Actions/purchaseOrderActions";
import SellerActions from "@Actions/sellerActions";
import Constants, { QUANTITY, INFINITE_EXPIRY_DATE } from "@Apis/constants";
import GRNActions from "@Actions/grnActions";
import CFV2Actions from "@Actions/configurations/cfV2Actions";
import InventoryLocationActions from "@Actions/settings/inventoryLocationActions";

// helpers
import Helpers from "@Apis/helpers";
import CustomFieldHelpers from "@Helpers/CustomFieldHelpers";
import {
  INITIAL_STATE,
  customInputChangeHelper,
  handleFullQuantityHelper,
  handleCreateGRNHelper,
  getAvailableBatchesHelper,
  handleDeleteHelper,
  updateSourceProductBatchesHelper,
  reverseTransformDataHelper,
  transformDataHelper,
} from './GrnHelpers';
import GrnErrors from "./grnErrors";


const uploadButton = (
  <div>
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  </div>
);

const GrnForm = (props) => {
  const {
    getGRN,
    user,
    match,
    MONEY,
    getGRNSuccess,
    inventoryLocations,
    cfV2DocGoodReceivingNotes,
    getDocCFV2,
    getInventoryLocations,
    TenantDepartmentId,
    getGRNByIdSuccess,
    getDocCFV2Success,
    addGRN,
    selectedSt,
    callback,
    stockTransferType,
    updateGRN,
    history,
    grnData,
    createGRNLoading,
    updateGRNStatusLoading,
    updateGRNLoading,
    getInventoryLocationsLoading,
  } = props;

  const [state, setState] = useState(INITIAL_STATE());
  const isSecondaryUomEnabled = user?.tenant_info?.global_config?.settings?.enable_secondary_uom;

  const {
    totalBatchWiseQty,
    selectedPoForGrn,
    selectedLineInfo,
    customFields,
    cfGoodReceivingNotesDoc,
    isUserReadyOne,
    grnTableData,
    grnOriginalData,
    sourceDepartmentInfo,
    destDepartmentInfo,
    destDepartmentId,
    isUserReadyForCreate,
    fileList,
    terms,
    invoiceNumber,
    grnDate,
    isUserReadyForUpdate,
    stockTransferNumber,
    deliveryDate,
    selectedLineId,
    toggleRejectedBatches,
    buttonClick,
    formSubmitted,
    grnNumber,
    docSeqId,
    initialGrnNumber,
  } = state;

  const updateState = (key, value) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  useEffect(() => {
    // ComponentDidMount Logic
    const payload = {
      orgId: user?.tenant_info?.org_id,
      entityName: "GOOD_RECEIVING_NOTES",
    };
    getDocCFV2(payload);

    if (match?.params?.grnId) {
      getGRN(
        Helpers.getTenantEntityPermission(
          user?.user_tenants,
          Helpers.permissionEntities.GOOD_RECEIVING,
          Helpers.permissionTypes.READ
        ).join(","),
        match?.params?.grnId
      );
    }
    getInventoryLocations(TenantDepartmentId, "", null, true);

    // ComponentWillUnmount Logic
    return () => {
      getGRNByIdSuccess(null);
      getDocCFV2Success(null);
    };
  }, [
    getGRN,
    getDocCFV2,
    getInventoryLocations,
    match?.params?.grnId,
    user,
    TenantDepartmentId,
    getGRNByIdSuccess,
    getDocCFV2Success,
  ]);

  useEffect(() => {
    if (cfV2DocGoodReceivingNotes && !isUserReadyOne) {
      const structuredFields =
        CustomFieldHelpers.getCfStructure(
          cfV2DocGoodReceivingNotes?.data?.document_custom_fields,
          true
        ) || [];

      setState((prevState) => ({
        ...prevState,
        cfGoodReceivingNotesDoc: structuredFields,
        isUserReadyOne: true,
      }));
    }
  }, [cfV2DocGoodReceivingNotes, isUserReadyOne]);

  useEffect(() => {
    if (selectedSt && !isUserReadyForCreate && inventoryLocations) {
      const stockTransferLine = selectedSt?.stock_transfer_lines.map(
        (line, i) => ({
          key: uuidv4(),
          quantity: line?.quantity,
          secondary_uom_qty: line?.secondary_uom_qty,
          remarks: line?.remarks,
          consumed_qty: line?.consumed_qty,
          product_batch_info: line?.product_batch_info,
          product_sku_info: line?.product_sku_info,
          source_batch_id: line?.source_batch_id,
          stock_transfer_id: line?.stock_transfer_id,
          stock_transfer_line_id: line?.stock_transfer_line_id,
          tenant_product_id: line?.tenant_product_id,
          total_available_batches_quantity:
            line?.total_available_batches_quantity,
          expiry_date: line?.product_batch_info?.expiry_date,
          uom_info: line?.uom_info,
          uomInfo: line?.uom_info,
          selectedBatch: line?.available_destination_product_batches?.filter(
            (batch) => !batch.is_rejected_batch
          )?.[0],
          available_batches: line?.available_destination_product_batches || [],
          expiryDays: line?.product_sku_info?.expiry_days,
          destination_tenant_product_id: line?.destination_tenant_product_id,
          source_product_batches: line?.source_product_batches?.map(
            (batch) => ({
              ...batch,
              inventory_location_id:
                inventoryLocations?.inventory_location?.[0]
                  ?.inventory_location_id,
              inventory_location_path:
                inventoryLocations?.inventory_location?.[0]
                  ?.inventory_location_path,
              inventory_location_name:
                inventoryLocations?.inventory_location?.[0]
                  ?.inventory_location_name,
            })
          ),
          nextBatchCode: `${
            line?.product_sku_info?.internal_sku_code
          }/${dayjs().format("DDMMYY")}/${
            line?.product_sku_info?.product_batch_counter
          }`,
          received_qty: QUANTITY(
            line?.quantity - line?.consumed_qty,
            line?.uom_info?.precision
          ),
          sq_no: i + 1, // Serial number
        })
      );

      setState((prevState) => ({
        ...prevState,
        grnTableData: transformDataHelper(stockTransferLine),
        grnOriginalData: stockTransferLine,
        sourceDepartmentInfo: selectedSt?.source_department_info,
        destDepartmentInfo: selectedSt?.destination_department_info,
        destDepartmentId: selectedSt?.destination_tenant_department_id,
        stockTransferNumber: selectedSt?.stock_transfer_number,
        isUserReadyForCreate: true,
      }));
    }
  }, [selectedSt, inventoryLocations]);

  useEffect(() => {
    if (
      grnData &&
      cfV2DocGoodReceivingNotes?.data?.document_custom_fields &&
      !selectedSt &&
      !isUserReadyForUpdate
    ) {
      const grnDataEntity = grnData?.grn?.[0];
      const oldCustomField = grnDataEntity?.custom_fields || [];

      const tempGrnLine = grnDataEntity?.grn_lines.map((grnLine) => ({
        key: uuidv4(),
        stock_transfer_line_id:
          grnLine?.stock_transfer_line_details?.stock_transfer_line_id,
        quantity: grnLine?.stock_transfer_line_details?.quantity,
        tenant_product_id: grnLine?.tenant_product_id,
        source_batch_id: grnLine?.stock_transfer_line_details?.source_batch_id,
        consumed_qty: grnLine?.stock_transfer_line_details?.consumed_qty,
        secondary_uom_qty: grnLine?.stock_transfer_line_details?.secondary_uom_qty,
        dc_quantity: grnLine?.stock_transfer_line_details?.dc_quantity,
        product_sku_info: grnLine?.product_sku_info,
        tax_info: grnLine?.tax_info,
        remarks: grnLine?.remarks,
        uom_info: grnLine?.uom_info?.[0],
        grn_entity_line_id: grnLine?.grn_entity_line_id,
        expiry_date: grnLine?.expiry_date,
        received_qty: grnLine?.received_qty,
        uomInfo: grnLine?.uom_info?.[0],
        available_batches: getAvailableBatchesHelper(grnLine),
        selectedBatch: getAvailableBatchesHelper(grnLine)?.[0],
        grn_id: grnLine?.grn_id,
        source_product_batches: grnLine?.product_batches,
        expiryDays: grnLine?.product_sku_info?.expiry_days,
        nextBatchCode: `${
          grnLine?.product_sku_info?.internal_sku_code
        }/${dayjs().format("DDMMYY")}/${
          grnLine?.product_sku_info?.product_batch_counter
        }`,
      }));

      setState((prevState) => ({
        ...prevState,
        fileList: grnDataEntity?.attachments,
        terms: grnDataEntity?.remark,
        grnTableData: transformDataHelper(tempGrnLine),
        grnOriginalData: tempGrnLine,
        originalData: tempGrnLine,
        invoiceNumber: grnDataEntity?.invoice_number,
        grnDate: dayjs(grnDataEntity?.grn_date_time),
        stockTransferNumber:
          grnDataEntity?.stock_transfers?.[0]?.stock_transfer_number,
        deliveryDate: dayjs(grnDataEntity?.invoiceDate),
        cfGoodReceivingNotesDoc: CustomFieldHelpers.getDocumentCf(
          cfV2DocGoodReceivingNotes?.data?.document_custom_fields,
          oldCustomField
        ),
        isUserReadyForUpdate: true,
        grnNumber: grnDataEntity?.grn_number || '',
      }));
    }
  }, [grnData, cfV2DocGoodReceivingNotes, selectedSt, isUserReadyForUpdate]);

  function customInputChange(fieldValue, cfId) {
    customInputChangeHelper(state, props, fieldValue, cfId, (newState) =>
      updateState("cfGoodReceivingNotesDoc", newState?.cfGoodReceivingNotesDoc)
    );
  }

  function handleDelete(key) {
    handleDeleteHelper(state, key, (newState) => {
      updateState("grnTableData", newState?.grnTableData);
      updateState("grnOriginalData", newState?.grnOriginalData);
    });
  }

  function handleCreateGRN(approve) {
    handleCreateGRNHelper(state, props, approve, updateGRN, (newState) => {
      updateState("formSubmitted", newState?.formSubmitted);
      updateState("buttonClick", newState?.buttonClick);
    });
  }

  function handleFullQuantity(item) {
    handleFullQuantityHelper(state, item, (newState) =>
      updateState("grnTableData", newState?.grnTableData)
    );
  }

  const newGrnColumns = [
    {
      title: "#",
      width: "10px",
      render: (text, record) => (
        <div>{record?.skuId ? <div>{record?.sq_no}</div> : ""}</div>
      ),
      onCell: (record) => {
        if (record?.skuId) {
          return { colSpan: 0.5 };
        }
        return { colSpan: 0.5 };
      },
    },
    {
      title: "PRODUCT NAME",
      render: (record) => {
        const expiryMoment = dayjs(
          dayjs(record.expiry_date).format("YYYY-MM-DD")
        );
        let shelfLife = 0;
        if (dayjs().isSameOrBefore(expiryMoment)) {
          shelfLife = expiryMoment.diff(
            dayjs(dayjs().format("YYYY-MM-DD")),
            "days"
          );
        }
        let shelfLifePct = Math.round((shelfLife / record?.expiryDays) * 100);
        if (record?.expiry_date === INFINITE_EXPIRY_DATE) shelfLifePct = 100;

        return {
          props: {
            style: {
              backgroundColor: record?.skuId ? "rgb(233, 242, 251)" : "white",
            },
          },
          children: (
            <div>
              {record?.skuId ? (
                <div>
                  <Link
                    to={`/inventory/product/view/${record?.product_sku_id}`}
                    target="_blank"
                  >
                    <div>#{record?.internal_sku_code}</div>
                  </Link>
                  {record?.product_category_info &&
                    record?.product_category_info?.category_path?.length >
                      0 && (
                      <ProductCategoryLabel
                        categoryPath={
                          record?.product_category_info?.category_path
                        }
                        categoryName={record?.product_category_info?.category_path?.at(
                          -1
                        )}
                        containerStyle={{
                          width: "fit-content",
                        }}
                      />
                    )}
                  <H3Text text={record?.product_sku_name} />
                  <textarea
                    style={{
                      width: "215px",
                    }}
                    value={record?.remarks}
                    onChange={(event) => {
                      const copyData = JSON.parse(JSON.stringify(grnTableData));
                      const updatedData = copyData.map((obj) => {
                        if (obj.key === record?.key) {
                          return {
                            ...obj,
                            remarks: event.target.value,
                          };
                        }

                        return obj;
                      });
                      // setGrnTableData(updatedData);
                      updateState("grnTableData", updatedData);
                    }}
                    className="grn-st-table__remarks"
                    placeholder="add description here.."
                  />
                </div>
              ) : (
                <div>
                  {record?.custom_batch_number || record?.batch_number}
                  {record?.expiryDays >= 0 && (
                    <div className="batch-shelf-life">
                      <div
                        className="progress-line__wrapper"
                        style={{
                          width: "80px",
                          backgroundColor:
                            shelfLifePct < 25
                              ? "rgba(216,51,15,0.2)"
                              : shelfLifePct > 75
                              ? "rgba(23,181,77,0.2)"
                              : "rgba(204,185,10,0.2)",
                        }}
                      >
                        <div
                          className="progress-line"
                          style={{
                            width: `${
                              shelfLifePct > 100 ? 100 : shelfLifePct
                            }%`,
                            backgroundColor:
                              shelfLifePct < 25
                                ? "#D8330F"
                                : shelfLifePct > 75
                                ? "#17B54D"
                                : "#CCB90A",
                          }}
                        />
                      </div>
                      <span className="table-subscript">
                        {record?.expiry_date === INFINITE_EXPIRY_DATE
                          ? "NA"
                          : `${shelfLife} Days (${shelfLifePct}%)`}
                      </span>
                    </div>
                  )}
                  <div
                    style={{
                      color: "rgb(45, 125, 247)",
                      fontSize: "11px",
                      fontWeight: "400",
                    }}
                  >
                    {record?.lot_number && (
                      <H3Text text={`Lot# ${record?.lot_number}`} />
                    )}
                    {record?.ar_number && (
                      <H3Text text={`AR# ${record?.ar_number}`} />
                    )}
                  </div>
                </div>
              )}
            </div>
          ),
        };
      },
      onCell: (record) => {
        if (record?.skuId) {
          return { colSpan: 5 };
        }
        return {};
      },
    },
    {
      title: "COST PRICE",
      render: (text, record) => (
        <div>
          {!record?.skuId ? (
            <div>{record?.cost_price ? MONEY(record?.cost_price) : "-"}</div>
          ) : (
            ""
          )}
        </div>
      ),
      onCell: (record) => {
        if (record?.skuId) {
          return { colSpan: 0 };
        }
        return { colSpan: 1 };
      },
    },
    {
      title: "QUALITY",
      render: (text, record) => (
        <div>
          {!record?.skuId ? (
            <div
              className="qr__status"
              style={{
                backgroundColor: record?.is_rejected_batch
                  ? "#ee2232"
                  : "#2fbc6e",
              }}
            >
              {record?.is_rejected_batch ? "Bad" : "Good"}
            </div>
          ) : (
            ""
          )}
        </div>
      ),
      onCell: (record) => {
        if (record?.skuId) {
          return { colSpan: 0 };
        }
        return { colSpan: 1 };
      },
    },
    {
      title: "PENDING QTY",
      render: (record) => ({
        children: (
          <div>
            {record?.skuId ? (
              ""
            ) : (
              <div>
                {QUANTITY(
                  (record?.available_qty || 0) -
                    (record?.batch_created_qty || 0),
                  record?.uom_info?.precision
                )}{" "}
                {record?.uom_info?.uqc?.toProperCase()}
              </div>
            )}
          </div>
        ),
      }),
      onCell: (record) => {
        if (record?.skuId) {
          return { colSpan: 0 };
        }
        return {};
      },
    },
    {
      title: "SHELF/RACK",
      render: (record) => {
        return {
          children: (
            <div>
              {record?.skuId ? (
                ""
              ) : (
                <div>
                  <WarehouseLocationSelector
                    containerClassName="multiple-batches__input-warehouse-location"
                    hideTitle
                    selectedInventoryLocation={record?.inventory_location_path}
                    onChange={(id, name) => {
                      const copyData = JSON.parse(JSON.stringify(grnTableData));
                      const updatedData = copyData.map((obj) => {
                        if (obj.key === record?.key) {
                          return {
                            ...obj,
                            inventory_location_id: id,
                            inventory_location_path: name,
                          };
                        }

                        return obj;
                      });
                      // setGrnTableData(updatedData);
                      updateState("grnTableData", updatedData);
                    }}
                    destDepartmentId={TenantDepartmentId}
                  />
                </div>
              )}
            </div>
          ),
        };
      },
      onCell: (record) => {
        if (record?.skuId) {
          return { colSpan: 0 };
        }
        return {};
      },
    },
    {
      title: "RECEIVING QTY",
      width: "150px",
      render: (record) => {
        const reverseTransformGrnTableData =
          reverseTransformDataHelper(grnTableData);
        const finalData = updateSourceProductBatchesHelper(
          grnOriginalData,
          reverseTransformGrnTableData
        );
        const totalReceivedQty = finalData
          ?.filter((item) => item?.key === record?.parent_key)
          ?.map(
            (item) =>
              item?.source_product_batches?.reduce(
                (acc, batch) => acc + Number(batch?.batch_wise_quantity || 0),
                0
              ) || 0
          )
          .reduce((acc, qty) => acc + qty, 0);

        return {
          props: {
            style: {
              backgroundColor: record?.skuId ? "rgb(233, 242, 251)" : "white",
            },
          },
          children: (
            <div>
              {record?.skuId ? (
                <div>
                  {QUANTITY(totalReceivedQty, record?.uom_info?.precision)}
                  {' '}
                  of
                  {' '}
                  {(record?.quantity || 0) - (record?.consumed_qty || 0)}
                  {' '}
                  received
                  <H3Text
                    text={<div>Full Quantity</div>}
                    onClick={() => handleFullQuantity(record)}
                    className="create-grn__qty"
                  />
                  {isSecondaryUomEnabled && record?.secondary_uom_id && (
                    <div
                      style={{
                        marginTop: '10px',
                      }}
                    >
                      <span className="uom-label">{`Secondary (${record.secondary_uom_info?.uqc ? record.secondary_uom_info?.uqc : 'N/A'})`}</span>
                      <H3FormInput
                        name="receiving qty less then or equal to pending qty"
                        type="number"
                        value={record?.secondary_uom_qty}
                        inputClassName="orgFormInput"
                        labelClassName="orgFormLabel"
                        disabled={createGRNLoading || updateGRNLoading || !record?.secondary_uom_id}
                        onChange={(event) => {
                          const copyData = JSON.parse(JSON.stringify(grnTableData));
                          const updatedData = copyData.map((obj) => {
                            if (obj.key === record?.key) {
                              return {
                                ...obj,
                                secondary_uom_qty: event.target.value,
                              };
                            }
                            return obj;
                          });
                          updateState('grnTableData', updatedData);
                        }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <H3FormInput
                    name="receiving qty less then or equal to pending qty"
                    type="number"
                    value={record?.batch_wise_quantity}
                    // inputClassName={`orgFormInput ${(Number(record?.batch_wise_quantity) > Number(record?.available_qty)) ? 'orgFormInputError' : ''}`}
                    inputClassName="orgFormInput"
                    labelClassName="orgFormLabel"
                    disabled={createGRNLoading || updateGRNLoading}
                    showError={
                      record?.batch_wise_quantity >
                      (record?.available_qty || 0) -
                        (record?.batch_created_qty || 0)
                    }
                    onChange={(event) => {
                      const copyData = JSON.parse(JSON.stringify(grnTableData));
                      const updatedData = copyData.map((obj) => {
                        if (obj.key === record?.key) {
                          return {
                            ...obj,
                            batch_wise_quantity: event.target.value,
                          };
                        }

                        return obj;
                      });
                      updateState("grnTableData", updatedData);
                      // setGrnTableData(updatedData);
                    }}
                  />
                </div>
              )}
            </div>
          ),
        };
      },
      onCell: (record) => {
        if (record?.skuId) {
          return { colSpan: 1 };
        }
        return { colSpan: 1 };
      },
    },
    {
      title: "",
      width: "30px",
      render: (record) => {
        // const { selectedLineInfo } = this.state;
        return {
          props: {
            style: {
              backgroundColor: record?.skuId ? "rgb(233, 242, 251)" : "white",
            },
          },
          children: (
            <div>
              {record?.skuId ? (
                <Popconfirm
                  title="Are you sure you want to remove this item?"
                  placement='topRight'
                  onConfirm={() =>
                    handleDelete(record?.parent_key, record?.grn_line_id)
                  }
                >
                  <div
                    className="delete-line-button__new"
                    style={{ marginLeft: "auto" }}
                  >
                    <FontAwesomeIcon icon={faTrashCan} />
                  </div>
                </Popconfirm>
              ) : (
                <div>{record?.uom_info?.uqc?.toProperCase()}</div>
              )}
            </div>
          ),
        };
      },
    },
  ];

  // counting the number of line items
  const count = grnTableData?.filter((item) => item.skuId !== undefined).length;

  // reverse transform data to original format
  const reverseTransformGrnTableData = reverseTransformDataHelper(grnTableData);

  // here we are updating the source product batches
  const finalData = updateSourceProductBatchesHelper(
    grnOriginalData,
    reverseTransformGrnTableData
  );

  // errors for error component
  const { docLevelError, lineLevelError } = GrnErrors({ grnDate, deliveryDate, cfGoodReceivingNotesDoc, grnTableData, grnNumber});

  return (
    <React.Fragment>
      <div
        className={`${
          match?.params?.grnId
            ? "form__wrapper form-component"
            : "form__drawer form-component"
        }`}
      >
        {(formSubmitted && (!!docLevelError?.length || !!lineLevelError?.length)) && (
          <ErrorHandle message='Mandatory fields required' docLevelErrors={docLevelError} lineLevelErrors={lineLevelError} />
        )}
        <div className="ant-row">
          <div className="ant-col-md-24">
            <div className="form__section">
              <div className="flex-display flex-align-c mg-bottom-5 pd-right-15">
                <H3Text text="PART A" className="form__section-title" />
                <div className="form__section-line" />
              </div>
              <div className="form__section-inputs mg-bottom-20">
                <div className="ant-row">
                  <DocumentNumberSeqInput
                    valueFromProps={grnNumber}
                    updateCase={match?.params?.grnId}
                    setInitialDocSeqNumber={(value) => updateState('initialGrnNumber', value)}
                    entityName="GOOD_RECEIVING_NOTE"
                    docSeqId={docSeqId}
                    tenantId={selectedSt?.destination_department_info?.tenant_id}
                    onChangeFromProps={(event, newValue, seqId) => {
                      updateState('grnNumber', newValue ? (newValue || '') : (event?.target?.value || ''));
                      updateState('docSeqId', seqId);
                    }}
                    docTitle="GRN#"
                    formSubmitted={formSubmitted}
                  />
                  {(selectedSt || grnData?.grn?.[0]?.stock_transfers) && (
                    <div className="ant-col-md-6">
                      <div className="form__input-row">
                        <H3Text
                          text="Source"
                          className="form__input-row__label"
                        />
                        <div className="form__input-row__input">
                          <H3Text
                            text={
                              `${
                                selectedSt?.source_department_info
                                  ?.tenant_name ||
                                grnData?.grn?.[0]?.stock_transfers?.[0]
                                  ?.source_department_info?.tenant_name ||
                                ""
                              } ` +
                              `(${
                                selectedSt?.source_department_info
                                  ?.department_name ||
                                grnData?.grn?.[0]?.stock_transfers?.[0]
                                  ?.source_department_info?.department_name ||
                                ""
                              })`
                            }
                            style={{
                              marginTop: "5px",
                              padding: "7px",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {(selectedSt || grnData?.grn?.[0]?.stock_transfers) && (
                    <div className="ant-col-md-6">
                      <div className="form__input-row">
                        <H3Text
                          text="Destination"
                          className="form__input-row__label"
                        />
                        <div className="form__input-row__input">
                          <H3Text
                            text={
                              `${
                                selectedSt?.destination_department_info
                                  ?.tenant_name ||
                                grnData?.grn?.[0]?.stock_transfers?.[0]
                                  ?.destination_department_info?.tenant_name ||
                                ""
                              } ` +
                              `(${
                                selectedSt?.destination_department_info
                                  ?.department_name ||
                                grnData?.grn?.[0]?.stock_transfers?.[0]
                                  ?.destination_department_info
                                  ?.department_name ||
                                ""
                              })`
                            }
                            style={{
                              marginTop: "5px",
                              padding: "7px",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="ant-col-md-6">
                    <div className="form__input-row">
                      <H3Text
                        text="Stock Transfer#"
                        className="form__input-row__label"
                      />
                      <div className="form__input-row__input">
                        <H3Text
                          text={stockTransferNumber}
                          style={{
                            marginTop: "5px",
                            padding: "7px",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="ant-col-md-6">
                    <div className="form__input-row orgInputContainer">
                      <H3Text
                        text="GRN Date"
                        required
                        className="form__input-row__label"
                      />
                      <div className={`form__input-row__input ${formSubmitted && !grnDate ? 'form__input-row__input-error' : ''}`}>
                        <DatePicker
                          value={grnDate}
                          onChange={(value) => {
                            updateState("grnDate", value);
                            // setGrnDate(value);
                          }}
                          disabledDate={(d) =>
                            !d ||
                            (user?.tenant_info?.purchase_config?.sub_modules
                              ?.goods_receiving_note?.settings
                              ?.allow_future_date_grn
                              ? null
                              : d.isAfter(
                                  dayjs().add(1, "days").format("YYYY-MM-DD")
                                )) ||
                            (user?.tenant_info?.purchase_config?.sub_modules
                              ?.goods_receiving_note?.settings
                              ?.allow_past_date_grn
                              ? null
                              : d.isSameOrBefore(dayjs().format("YYYY-MM-DD")))
                          }
                          disabled={createGRNLoading || updateGRNLoading}
                          style={{
                            border: "1px solid rgba(68, 130, 218, 0.2)",
                            borderRadius: "2px",
                            height: "28px",
                            padding: "1px 3px",
                            width: "100%",
                            background: "white",
                          }}
                        />
                        {/* {formSubmitted && !grnDate && (
                          <div className="formError">
                            *Please select Grn Date
                          </div>
                        )} */}
                      </div>
                    </div>
                  </div>
                  <div className="ant-col-md-6">
                    <div className="form__input-row">
                      <H3Text text="DC#" className="form__input-row__label" />
                      <div className="form__input-row__input">
                        <H3FormInput
                          name="delivery challan"
                          type="text"
                          containerClassName="orgInputContainer create-grn__input-row "
                          labelClassName="orgForm__label"
                          inputClassName="orgFormInput input"
                          placeholder=""
                          disabled={createGRNLoading || updateGRNLoading}
                          onChange={(event) =>
                            updateState("invoiceNumber", event.target.value)
                          }
                          value={invoiceNumber}
                          maxlength="50"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="ant-col-md-6">
                    <div className="form__input-row">
                      <H3Text
                        text="DC Date"
                        required
                        className="form__input-row__label"
                      />
                      <div className={`form__input-row__input ${formSubmitted && !deliveryDate ? 'form__input-row__input-error' : ''}`}>
                        <DatePicker
                          value={deliveryDate}
                          onChange={(value) => {
                            updateState("deliveryDate", value);
                            // setDeliveryDate(value);
                          }}
                          disabled={createGRNLoading || updateGRNLoading}
                          style={{
                            border: "1px solid rgba(68, 130, 218, 0.2)",
                            borderRadius: "2px",
                            height: "28px",
                            padding: "1px 3px",
                            width: "100%",
                            background: "white",
                          }}
                        />
                        {/* {formSubmitted && !deliveryDate && (
                          <div className="formError">
                            *Please select Delivery Challan Date
                          </div>
                        )} */}
                      </div>
                    </div>
                  </div>
                  <CustomFieldV3
                    customFields={cfGoodReceivingNotesDoc || []}
                    formSubmitted={formSubmitted}
                    customInputChange={(value, cfId) =>
                      customInputChange(value, cfId)
                    }
                    disableCase={createGRNLoading || updateGRNStatusLoading}
                    hideTitle
                    wrapperClassName="ant-col-md-6"
                    containerClassName="form__input-row"
                    labelClassName="form__input-row__label"
                    inputClassName="form__input-row__input"
                    errorClassName="form__input-row__input-error"
                    isCarryForward={!match?.params?.grnId}
                  />
                </div>
              </div>
            </div>
            <div className="form__section">
              <div className="flex-display flex-align-c mg-bottom-5 pd-right-15">
                <H3Text text="PART B" className="form__section-title " />
                <div className="form__section-line" />
              </div>
              <div className="ant-row">
                <div className="form__input-row">
                  <H3Text
                    text="Additional Remarks"
                    className="form__input-row__label"
                  />
                  <div className="form__input-row__input">
                    <textarea
                      rows="2"
                      cols="50"
                      onChange={(event) => {
                        updateState("terms", event.target.value);
                        // setTerms(event.target.value);
                      }}
                      value={terms}
                      style={{
                        resize: "none",
                        backgroundColor: "rgb(239, 239, 239)",
                        border: "none",
                        borderRadius: "4px",
                        marginRight: "15px",
                        height: "103px",
                        padding: "4px 8px",
                      }}
                    />
                  </div>
                </div>
                <div style={{ marginTop: "10px" }}>
                  <label className="orgFormLabel">Attachment(s)</label>
                  <Upload
                    action={Constants.UPLOAD_FILE}
                    listType="picture-card"
                    fileList={fileList}
                    disabled={createGRNLoading || updateGRNLoading}
                    onChange={(fileListData) => {
                      setState((prevState) => ({
                        ...prevState,
                        fileList: fileListData?.fileList?.map((item) => ({
                          ...item,
                          url: item?.response?.response?.location || item?.url,
                        })),
                      }));
                    }}
                    multiple
                  >
                    {fileList?.length >= 20 ? null : uploadButton}
                  </Upload>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="form_lines-wrapper">
          <Table
            bordered
            showHeader
            loading={
              createGRNLoading ||
              updateGRNLoading ||
              getInventoryLocationsLoading
            }
            title={() => `GRN Line Items (${count || "0"})`}
            size="small"
            columns={newGrnColumns}
            dataSource={grnTableData}
            pagination={false}
            scroll={{ x: "max-content" }}
          />
        </div>
      </div>
      <div className="create-grn__footer">
        {Helpers.getPermission(
          Helpers.permissionEntities.GOOD_RECEIVING,
          Helpers.permissionTypes.CREATE,
          user
        ) && (
          <H3Button
            id="save-as-draft"
            text="Save as Draft"
            buttonType={defaultButtonTypes.BLUE_ROUNDED}
            customClass="create-grn__footer-draft"
            onClick={() => {
              if (!createGRNLoading) {
                handleCreateGRN(false, "DRAFT");
              }
            }}
            isLoading={
              (createGRNLoading ||
                updateGRNStatusLoading ||
                updateGRNLoading) &&
              buttonClick === "DRAFT"
            }
            disabled={
              createGRNLoading || updateGRNStatusLoading || updateGRNLoading
            }
          />
        )}
        {Helpers.getPermission(
          Helpers.permissionEntities.GOOD_RECEIVING,
          Helpers.permissionTypes.CREATE,
          user
        ) && (
          <H3Button
            id="save-and-issue"
            text="Save and Issue"
            buttonType={defaultButtonTypes.BLUE_ROUNDED}
            customClass="create-grn__footer-submit"
            onClick={() => {
              updateState("buttonClick", "ISSUED");
              if (!createGRNLoading) {
                handleCreateGRN(true, "ISSUED");
              }
            }}
            isLoading={
              (createGRNLoading ||
                updateGRNStatusLoading ||
                updateGRNLoading) &&
              buttonClick === "ISSUED"
            }
            disabled={
              createGRNLoading || updateGRNStatusLoading || updateGRNLoading
            }
          />
        )}
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = ({
  UserReducers,
  SellerReducers,
  GRNReducers,
  CFV2Reducers,
  InventoryLocationReducers,
}) => ({
  user: UserReducers.user,
  createGRNLoading: GRNReducers.createGRNLoading,
  updateGRNStatusLoading: GRNReducers.updateGRNStatusLoading,
  grnData: GRNReducers.grnData,
  sellers: SellerReducers.sellers,
  getDocCFV2Loading: CFV2Reducers.getDocCFV2Loading,
  cfV2DocGoodReceivingNotes: CFV2Reducers.cfV2DocGoodReceivingNotes,
  updateGRNLoading: GRNReducers.updateGRNLoading,
  MONEY: UserReducers.MONEY,
  inventoryLocations: InventoryLocationReducers.inventoryLocations,
  getInventoryLocationsLoading:
    InventoryLocationReducers.getInventoryLocationsLoading,
});

const mapDispatchToProps = (dispatch) => ({
  addGRN: (payload, action) => dispatch(GRNActions.createGRN(payload, action)),
  updateGRN: (payload, action) =>
    dispatch(GRNActions.updateGRN(payload, action)),
  updateGRNStatus: (payload, action) =>
    dispatch(GRNActions.updateGRNStatus(payload, action)),
  getSellers: (keyword, tenantId, page, limit, sellerId, callback) =>
    dispatch(
      SellerActions.getSellers(
        keyword,
        tenantId,
        page,
        limit,
        sellerId,
        callback
      )
    ),
  getGRN: (page, poId) => dispatch(GRNActions.getGRN(page, poId)),
  getDocCFV2: (payload, callback) =>
    dispatch(CFV2Actions.getDocCFV2(payload, callback)),
  getDocCFV2Success: (customFields) =>
    dispatch(CFV2Actions.getDocCFV2Success(customFields)),
  getGRNByIdSuccess: (selectedGrn) =>
    dispatch(GRNActions.getGRNByIdSuccess(selectedGrn)),
  getInventoryLocations: (
    tenantDepartmentId,
    inventoryLocationPath,
    parentLocationId,
    isStorable
  ) =>
    dispatch(
      InventoryLocationActions.getInventoryLocations(
        tenantDepartmentId,
        inventoryLocationPath,
        parentLocationId,
        isStorable
      )
    ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(GrnForm));
